import { useCallback, useState } from "react";

// Third parties
import { useDispatch } from "react-redux";

// Evergiving
import { NonPaymentFormConfig } from "@evergiving/common/dist/es2015/form";
import { Donor, SubmissionResult } from "@evergiving/common/dist/es2015/pledge";

// Form components
import { SubmitButton } from "@/lib/components/SubmitButton";
import { SubmissionInProgressOverlay } from "@/lib/components/SubmissionInProgressOverlay";
import { TermsAndConditionsComponent } from "@/lib/components/TermsAndConditionsComponent";
import { Text } from "@/lib/components/Text";

// Hooks
import { useAfterAddressFieldCommon } from "@/lib/hooks/useAfterAddressFieldCommon";
import { Submission, SubmissionState } from "@/lib/hooks/useSubmissionState";
import { useFormSubmission } from "@/lib/hooks/useFormSubmission";
import { useFormRouter } from "@/lib/hooks/useFormRouter";

// Redux
import { initialStateFromFormConfig } from "@/lib/redux/store";
import { setPartialState } from "@/lib/redux/actions";

export const AfterAddressFieldLead = ({
  formConfig,
}: {
  formConfig: NonPaymentFormConfig;
}) => {
  const { anyFormErrors, canSubmitDonor, partialDonor, donorFields } =
    useAfterAddressFieldCommon({
      formConfig,
    });

  const formSubmission = useFormSubmission(formConfig);

  const dispatch = useDispatch();

  const formRouter = useFormRouter();

  const [submission, setSubmissionState] = useState<Submission>({
    state: SubmissionState.Unsubmitted,
  });

  const handleCloseSubmissionDialog = useCallback(() => {
    setSubmissionState({ state: SubmissionState.Unsubmitted });
  }, []);

  const canSubmit = !anyFormErrors && canSubmitDonor(donorFields);

  const handleSubmission = useCallback(async () => {
    if (!canSubmitDonor(donorFields)) {
      throw new Error("Unexpected state");
    }

    const donor: Donor = {
      ...partialDonor,
      name: {
        ...partialDonor.name,
        ...donorFields,
      },
    };

    setSubmissionState({ state: SubmissionState.Submitting });

    const response = await formSubmission.submit({ donor });
    const { result } = response;

    switch (result) {
      case "clientError":
      case SubmissionResult.GeneralError:
      case SubmissionResult.StripeError:
      case SubmissionResult.StripeRequiresAction:
        setSubmissionState({ state: SubmissionState.SubmissionError });
        break;
      case "timeoutError":
      case SubmissionResult.Success:
        dispatch(setPartialState(initialStateFromFormConfig(formConfig)));
        formRouter.replaceWithPage("thanks");
        break;
      default:
        ((x: never) => {
          throw new Error(`${String(x)} was unhandled!`);
        })(result);
    }
  }, [
    canSubmitDonor,
    donorFields,
    partialDonor,
    formSubmission,
    dispatch,
    formConfig,
    formRouter,
  ]);

  const termsAndConditionsFieldConfig = formConfig.termsAndConditions;

  return (
    <>
      {termsAndConditionsFieldConfig ? (
        <TermsAndConditionsComponent
          termsAndConditionsFieldConfig={termsAndConditionsFieldConfig}
        />
      ) : null}

      {anyFormErrors && formConfig.formErrorsText ? (
        <p>
          <Text value={formConfig.formErrorsText} />
        </p>
      ) : null}

      <SubmitButton
        canSubmit={
          canSubmit && submission.state === SubmissionState.Unsubmitted
        }
        onSubmit={handleSubmission}
        submitButtonText={formConfig.submitButtonText}
      />

      <SubmissionInProgressOverlay
        onClose={handleCloseSubmissionDialog}
        submission={submission}
      />
    </>
  );
};
